"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

type Theme = "dark" | "light";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "dark",
  toggleTheme: () => {},
  setTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

const STORAGE_KEY = "sofia-connect-theme";

function readInitialTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (saved === "light" || saved === "dark") return saved;
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    return prefersDark ? "dark" : "light";
  } catch {
    return "dark";
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Lazy init: reads localStorage/matchMedia only on client, once per mount.
  // SSR returns "dark"; client hydrates with the real value on first render.
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  // Sync the DOM attribute whenever the theme changes (incl. after hydration).
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    // Add transition class
    document.documentElement.setAttribute("data-theme-transitioning", "");
    document.documentElement.setAttribute("data-theme", newTheme);
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);

    // Remove transition class after animation
    setTimeout(() => {
      document.documentElement.removeAttribute("data-theme-transitioning");
    }, 350);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
